<script module lang="ts">
  export type UsersHomeLayoutSection = 'user' | 'public' | 'network'
</script>
<script lang="ts">
  import { debounce } from 'lodash-es'
  import app from '#app/app'
  import { resizeObserver } from '#app/lib/components/actions/resize_observer'
  import { getViewportHeight } from '#app/lib/screen'
  import { onChange } from '#app/lib/svelte/svelte'
  import { commands } from '#app/radio'
  import GroupProfile from '#groups/components/group_profile.svelte'
  import type { SerializedGroup } from '#groups/lib/groups'
  import type { FocusedSection } from '#inventory/types/inventory_browser'
  import type { SerializedShelf } from '#shelves/lib/shelves'
  import { mainUser } from '#user/lib/main_user'
  import { mainUserStore } from '#user/lib/serialized_main_user'
  import NetworkUsersNav from '#users/components/network_users_nav.svelte'
  import PublicUsersNav from '#users/components/public_users_nav.svelte'
  import UserProfile from '#users/components/user_profile.svelte'
  import UsersHomeNav from '#users/components/users_home_nav.svelte'
  import type { SerializedUser } from '#users/lib/users'

  let {
    user = $bindable(),
    group,
    shelf = $bindable(),
    section = $bindable(),
    profileSection = $bindable(),
    showShelfFollowers,
    showUserFollowers,
  }: {
    user?: SerializedUser
    group?: SerializedGroup
    shelf?: SerializedShelf | 'without-shelf'
    section?: UsersHomeLayoutSection
    profileSection?: 'inventory' | 'listings'
    showShelfFollowers?: boolean
    showUserFollowers?: boolean
  } = $props()

  // The focus state is used to determine which component should claim the focus
  // It plays the role of an event bus between the layout children component
  // to allow url navigation and scroll within the layout
  const focusedSection: FocusedSection = $derived.by(() => {
    if (shelf) return { type: 'shelf' }
    if (user) return { type: 'user' }
    if (group) return { type: 'group' }
    return { type: null }
  })

  $effect(() => {
    if (user) {
      if ('deleted' in user && user.deleted) commands.execute('show:error:missing')
    }
  })

  if (user && user._id === mainUser?._id) section = 'user'

  function onSectionChange () {
    if (section) {
      if (section === 'user') {
        user = $mainUserStore
      } else {
        shelf = null
      }
    }
  }
  $effect(() => {
    onChange(section, onSectionChange)
  })

  let wrapperEl: HTMLElement = $state()
  let innerEl: HTMLElement = $state()

  function onElementResize () {
    if (wrapperEl && innerEl) {
      const minHeight = Math.max(innerEl.clientHeight, getViewportHeight())
      // Set a minimun height so that the view doesn't jump up when the content height suddenly shrinks
      // Known cases:
      // - when then content is shorter, such as when a filter is picked and only a few items are displayed
      // - during a transition between two views, such as when a shelf is closed
      wrapperEl.style.minHeight = `${minHeight}px`
    }
  }

  function onFocus () {
    const pathname = `users/${section}`
    app.navigate(pathname, { pageSectionElement: wrapperEl })
  }

  const debouncedOnFocus = debounce(onFocus, 200, { leading: true })

  $effect(() => {
    if (focusedSection.type == null) debouncedOnFocus()
  })
</script>

<div class="wrapper" bind:this={wrapperEl}>
  <div
    id="usersHomeLayout"
    bind:this={innerEl}
    use:resizeObserver={{ onElementResize }}
  >
    {#if mainUser}
      <UsersHomeNav bind:section />
    {/if}

    {#if section === 'network'}
      <NetworkUsersNav {focusedSection} />
    {:else if section === 'public'}
      <PublicUsersNav {focusedSection} />
    {:else if user}
      <!-- Use #key to prevent reusing the previous UserProfile component,
           which might have been initialized with another user -->
      {#key user}
        <UserProfile
          {user}
          bind:shelf
          bind:profileSection
          {focusedSection}
          standalone={true}
          {showShelfFollowers}
          {showUserFollowers}
        />
      {/key}
    {:else if group}
      {#key group}
        <GroupProfile
          {group}
          {profileSection}
          {focusedSection}
          standalone={true}
        />
      {/key}
    {/if}
  </div>
</div>

<style lang="scss">
  @import "general/scss/utils";

  .wrapper{
    transition: min-height 0.5s 5s ease;
  }

  #usersHomeLayout{
    background-color: white;
    min-height: 100vh;

    /* Large screens */
    @media screen and (width >= $small-screen){
      padding: 0.5em;
    }
  }
</style>
